(() => {
  'use strict';
  if (customElements.get('cs-halloween-campaign')) return;
  const stores = Object.freeze({
    'plaza-san-miguel': 'Plaza San Miguel', 'santa-anita': 'Santa Anita',
    megaplaza: 'MegaPlaza', puruchuco: 'Puruchuco', 'plaza-norte': 'Plaza Norte',
    'mall-del-sur': 'Mall del Sur', bellavista: 'Bellavista'
  });
  const knownStore = (code) => Object.prototype.hasOwnProperty.call(stores, code);
  const objectList = (value) => value.split(',').map((item) => item.trim()).filter(Boolean);

  async function compressPhoto(file) {
    if (!file || !file.type.startsWith('image/') || file.size > 20 * 1024 * 1024) {
      throw new Error('Elige una imagen de hasta 20 MB.');
    }
    const url = URL.createObjectURL(file);
    try {
      const photo = new Image();
      photo.src = url;
      await photo.decode();
      const scale = Math.min(1, 1600 / Math.max(photo.naturalWidth, photo.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(photo.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(photo.naturalHeight * scale));
      const context = canvas.getContext('2d');
      if (!context) throw new Error('No se pudo preparar la foto.');
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(photo, 0, 0, canvas.width, canvas.height);
      const encoded = canvas.toDataURL('image/jpeg', 0.75);
      const base64 = encoded.split(',')[1];
      if (!base64 || base64.length > 2800000) throw new Error('La foto sigue siendo demasiado grande.');
      return { mime_type: 'image/jpeg', base64 };
    } catch {
      throw new Error('No pudimos preparar esta foto. Prueba con una imagen JPEG o PNG de hasta 20 MB.');
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  class Campaign extends HTMLElement {
    connectedCallback() {
      if (this.csLifecycle) return;
      this.csLifecycle = new AbortController();
      const listen = (node, event, handler) => node.addEventListener(event, handler, { signal: this.csLifecycle.signal });
      this.form = this.querySelector('[data-cs-form]');
      if (!this.form) return;
      this.button = this.querySelector('[data-cs-submit]');
      this.status = this.querySelector('[data-cs-status]');
      this.busy = false;
      this.completed = false;
      this.objectsReady = false;
      this.selected = new Map();
      this.online = this.dataset.channel === 'online';
      const documentType = this.form.elements.tipo_documento;
      const documentNumber = this.form.elements.numero_documento;
      const updateDocument = () => {
        const dni = documentType.value === 'dni';
        documentNumber.inputMode = dni ? 'numeric' : 'text';
        documentNumber.minLength = dni ? 8 : 1;
        documentNumber.maxLength = dni ? 8 : 60;
        if (dni) documentNumber.pattern = '[0-9]{8}';
        else documentNumber.removeAttribute('pattern');
        documentNumber.title = dni ? 'Ingresa exactamente 8 dígitos.' : 'Ingresa tu número de documento (hasta 60 caracteres).';
        documentNumber.placeholder = ({ dni: 'Ingresa tu DNI', ce: 'Ingresa tu número de carné',
          pasaporte: 'Ingresa tu número de pasaporte', otro: 'Ingresa tu número de documento' })[documentType.value];
        documentNumber.setCustomValidity('');
      };
      updateDocument();
      listen(documentType, 'change', updateDocument);
      listen(documentNumber, 'input', () => documentNumber.setCustomValidity(''));
      this.available = this.dataset.enabled === 'true' && this.dataset.termsReady === 'true' && this.validEndpoint();
      this.button.disabled = !this.available;
      if (!this.available) this.message('El registro de la campaña aún no está disponible. Vuelve a intentarlo cuando esté habilitado.');
      if (this.online) {
        const hotspots = [...this.querySelectorAll('[data-cs-hotspot]')];
        const next = this.querySelector('[data-cs-continue]');
        const game = this.querySelector('[data-cs-game]') || this.querySelector('.cs-halloween__game');
        const registration = this.querySelector('[data-cs-registration]');
        if (registration) registration.hidden = true;
        next.disabled = true;
        hotspots.forEach((spot) => {
          // Keep configured hit areas inside the image even at its edges.
          for (const suffix of ['', '-m']) {
            const x = parseFloat(spot.style.getPropertyValue(`--cs-x${suffix}`));
            const y = parseFloat(spot.style.getPropertyValue(`--cs-y${suffix}`));
            const w = parseFloat(spot.style.getPropertyValue(`--cs-w${suffix}`));
            const h = parseFloat(spot.style.getPropertyValue(`--cs-h${suffix}`));
            spot.style.setProperty(`--cs-x${suffix}`, `${Math.min(x, 100 - w)}%`);
            spot.style.setProperty(`--cs-y${suffix}`, `${Math.min(y, 100 - h)}%`);
          }
          listen(spot, 'click', () => {
            const id = spot.dataset.csObjectId;
            if (this.selected.has(id)) this.selected.delete(id);
            else this.selected.set(id, spot.dataset.csHotspot);
            spot.setAttribute('aria-pressed', String(this.selected.has(id)));
            this.objectsReady = false;
            this.form.elements.objects_found.value = '';
            next.disabled = this.selected.size === 0;
            this.querySelector('[data-cs-game-status]').textContent = `${this.selected.size} de ${hotspots.length} objetos seleccionados.`;
            this.querySelector('[data-cs-selected-summary]').textContent = 'Pulsa Continuar para confirmar tus objetos.';
          });
        });
        listen(next, 'click', () => {
          if (!this.selected.size) return;
          const names = [...this.selected.values()];
          this.form.elements.objects_found.value = JSON.stringify(names);
          this.objectsReady = true;
          if (this.available) this.message('');
          this.querySelector('[data-cs-selected-summary]').textContent = `Objetos seleccionados: ${names.join(', ')}.`;
          if (game) game.hidden = true;
          if (registration) registration.hidden = false;
          this.scrollToTarget(this.form);
          this.form.elements.nombre_completo.focus({ preventScroll: true });
        });
      } else {
        const select = this.form.elements.store_code;
        const code = new URL(window.location.href).searchParams.get('store');
        if (knownStore(code)) {
          select.value = code;
          const summary = this.querySelector('[data-cs-store-summary]');
          summary.textContent = `Estás participando desde: Central Sport – ${stores[code]}`;
          summary.hidden = false;
          this.querySelector('[data-cs-store-picker]').hidden = true;
        }
        listen(this.form.elements.objects_raw, 'input', () => this.form.elements.objects_raw.setCustomValidity(''));
      }
      listen(this.form, 'submit', (event) => this.submit(event));
    }

    disconnectedCallback() {
      this.csLifecycle?.abort();
      this.csRequest?.abort();
      this.csLifecycle = null;
    }

    validEndpoint() {
      try {
        const url = new URL(this.dataset.endpoint);
        return url.protocol === 'https:' && url.hostname === 'script.google.com' && /^\/macros\/s\/[^/]+\/exec$/.test(url.pathname);
      } catch { return false; }
    }

    message(text) { this.status.textContent = text; }
    scrollToTarget(target) {
      target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy || this.completed || !this.available) return;
      const value = (name) => this.form.elements[name].value.trim();
      this.form.elements.numero_documento.value = value('numero_documento');
      const documentNumber = value('numero_documento');
      this.form.elements.numero_documento.setCustomValidity(!documentNumber || /[\u0000-\u001f\u007f]/.test(documentNumber)
        ? 'Ingresa un número de documento válido.' : '');
      this.form.elements.correo_electronico.value = value('correo_electronico').toLowerCase();
      if (!this.online) {
        this.form.elements.objects_raw.setCustomValidity(objectList(value('objects_raw')).length === 3 ? '' : 'Escribe exactamente 3 objetos separados por comas.');
      }
      if (!this.form.reportValidity()) return;
      if (!value('nombre_completo') || !value('numero_contacto')) {
        this.message('Completa tu nombre y número de celular.'); return;
      }
      if (this.online && !this.objectsReady) {
        this.message('Selecciona los objetos de la imagen y pulsa Continuar antes de registrarte.'); return;
      }
      if (!this.online && !knownStore(value('store_code'))) {
        this.message('Selecciona una tienda válida.'); return;
      }
      const payload = {
        form_id: this.online ? 'halloween_2026_online' : 'halloween_2026_store',
        channel: this.online ? 'online' : 'store',
        nombre_completo: value('nombre_completo'), tipo_documento: value('tipo_documento'), numero_documento: documentNumber,
        correo_electronico: value('correo_electronico'), numero_contacto: value('numero_contacto'),
        terms_accepted: this.form.elements.terms_accepted.checked, website: value('website')
      };
      this.busy = true;
      this.button.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.form.querySelector('fieldset').disabled = true;
      this.message('Preparando tu participación…');
      const diagnostic = { code: 'preparation', httpStatus: null, responseType: null, formIdMatches: null };
      let timedOut = false;
      const rejectResponse = (code) => { diagnostic.code = code; throw new Error('Unconfirmed submission'); };
      try {
        if (this.online) payload.objects_found = JSON.parse(value('objects_found'));
        else {
          payload.store_code = value('store_code');
          payload.objects_raw = value('objects_raw');
          payload.photo = await compressPhoto(this.form.elements.photo.files[0]);
        }
        this.csRequest = new AbortController();
        const timeout = setTimeout(() => { timedOut = true; this.csRequest.abort(); }, 45000);
        try {
          this.message('Enviando tu participación…');
          diagnostic.code = 'network';
          // Simple POST avoids preflight. A readable acknowledgement is mandatory.
          const response = await fetch(this.dataset.endpoint, {
            method: 'POST', mode: 'cors', redirect: 'follow', credentials: 'omit',
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body: JSON.stringify(payload), signal: this.csRequest.signal
          });
          diagnostic.httpStatus = response.status;
          diagnostic.responseType = response.type;
          if (response.type === 'opaque' || response.type === 'opaqueredirect') rejectResponse('opaque_response');
          if (!response.ok) rejectResponse('http_error');
          diagnostic.code = 'invalid_json';
          const result = await response.json();
          if (!result || typeof result !== 'object' || Array.isArray(result)) rejectResponse('invalid_response');
          diagnostic.formIdMatches = result.form_id === payload.form_id;
          if (result.status !== 'ok') rejectResponse('backend_status');
          if (!diagnostic.formIdMatches) rejectResponse('form_id_mismatch');
          this.completed = true;
          this.form.hidden = true;
          const confirmation = this.querySelector('[data-cs-confirmation]');
          confirmation.hidden = false;
          confirmation.focus();
        } finally { clearTimeout(timeout); }
      } catch (error) {
        if (error.name === 'AbortError') diagnostic.code = timedOut ? 'timeout' : 'aborted';
        // Only bounded transport metadata: never payload, identifiers, response body or raw error text.
        console.warn('[CS Halloween] No se confirmó el registro.', diagnostic);
        this.message(error.message.startsWith('No pudimos') || error.message.startsWith('Elige una imagen')
          ? error.message : 'No pudimos confirmar tu registro. Revisa tu conexión e inténtalo nuevamente.');
      } finally {
        this.busy = false;
        this.form.removeAttribute('aria-busy');
        this.form.querySelector('fieldset').disabled = false;
        this.button.disabled = this.completed || !this.available;
      }
    }
  }
  customElements.define('cs-halloween-campaign', Campaign);
})();
